// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminBacktest.js
// ASX pre-open positive-announcement backtest: each trading day, announcements
// rated >= min significance and positive lodged between the previous close
// (16:00 Sydney) and the open (10:00) are bought at the open with an equal
// split of the compounding balance and sold at the close.
// Data: /api/admin/backtest/preopen-asx (announcement_backtest.py).
// Sections: tiles, equity curve, daily result bars (click jumps to the day),
// by rating, all trades ranked, by stock (winners | losers), day cards.
// Every % bar is diverging from the centre: green right, red left. Bars share
// one scale (95th percentile of |%|) so a single outlier doesn't flatten the rest.
// All stats beyond the API summary are worked out here from the returned trades.
import React, { useState, useCallback, useMemo } from 'react';
import Navbar from '../components/Navbar';
import api from '../api';

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '24px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '24px' },
  section: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'calc(var(--radius) * 2)', padding: '24px', marginBottom: '24px',
  },
  sectionHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' },
  sectionTitle: { fontSize: '15px', fontWeight: 600 },
  mono: { fontFamily: "'DM Mono', monospace" },
  row:  { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  input: {
    padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    fontSize: '13px', fontFamily: "'DM Sans', sans-serif", background: 'var(--bg)',
    color: 'var(--text)', outline: 'none', width: '110px',
  },
  btn: {
    padding: '7px 16px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: 500, fontFamily: "'DM Sans', sans-serif",
    background: 'var(--accent)', color: '#fff',
  },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' },
  tile:  { background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px' },
  tileLabel: { fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'DM Mono', monospace", marginBottom: '6px' },
  tileValue: { fontSize: '20px', fontWeight: 600, fontFamily: "'DM Mono', monospace" },
  muted: { color: 'var(--text-muted)', fontSize: '13px' },
  err:   { color: 'var(--error)', fontSize: '13px', marginBottom: '12px' },
  colHead: {
    fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em',
    fontFamily: "'DM Mono', monospace", padding: '0 12px 8px', borderBottom: '1px solid var(--border)',
  },
  // ticker | headline | sig | open→close | bar | % | $
  tradeGrid: {
    display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr) 36px 150px 140px 72px 96px',
    gap: '12px', alignItems: 'center', padding: '8px 12px', fontSize: '13px',
    borderBottom: '1px solid var(--border)',
  },
  headline: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  right: { textAlign: 'right' },
  seg:   { display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' },
  segBtn: {
    padding: '5px 12px', fontSize: '12px', border: 'none', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", background: 'var(--bg)', color: 'var(--text-muted)',
  },
  segOn: { background: 'var(--accent)', color: '#fff' },
  dayCard: { border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: '12px', overflow: 'hidden', scrollMarginTop: '80px' },
  dayHead: { display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: '13px' },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '24px' },
  stockGrid: {
    display: 'grid', gridTemplateColumns: '64px 48px 48px 140px 72px 96px',
    gap: '12px', alignItems: 'center', padding: '8px 12px', fontSize: '13px',
    borderBottom: '1px solid var(--border)',
  },
  rateGrid: {
    display: 'grid', gridTemplateColumns: '60px 70px 80px 140px 80px 110px',
    gap: '12px', alignItems: 'center', padding: '10px 12px', fontSize: '13px',
    borderBottom: '1px solid var(--border)',
  },
};

const money = v => (v < 0 ? '-$' : '$') + Math.abs(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct   = v => (v > 0 ? '+' : '') + v.toFixed(2) + '%';
const col   = v => v > 0 ? 'var(--success)' : v < 0 ? 'var(--error)' : 'var(--text-muted)';
const fmtT  = iso => new Date(iso).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const mean  = a => a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;
const fmtD  = iso => new Date(iso + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

// Diverging bar: centre line, green grows right, red grows left, clamped at the scale.
function PctBar({ v, scale, width = 140 }) {
  const w = Math.min(Math.abs(v) / scale, 1) * 50;
  return (
    <div style={{ position: 'relative', width: `${width}px`, height: '10px' }}>
      <div style={{ position: 'absolute', left: '50%', top: '-2px', bottom: '-2px', width: '1px', background: 'var(--border)' }} />
      <div style={{
        position: 'absolute', top: 0, bottom: 0, borderRadius: '2px', background: col(v),
        left: v >= 0 ? '50%' : `${50 - w}%`, width: `${w}%`,
      }} />
    </div>
  );
}

function Seg({ value, onChange, options }) {
  return (
    <div style={s.seg}>
      {options.map(([k, label]) => (
        <button key={k} style={{ ...s.segBtn, ...(value === k ? s.segOn : {}) }} onClick={() => onChange(k)}>{label}</button>
      ))}
    </div>
  );
}

function Curve({ days, start }) {
  if (!days.length) return null;
  const W = 1000, H = 240, P = 8;
  const vals = [start, ...days.map(d => d.balance)];
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const span = hi - lo || 1;
  const px = i => P + (i / (vals.length - 1 || 1)) * (W - 2 * P);
  const py = v => H - P - ((v - lo) / span) * (H - 2 * P);
  const pts = vals.map((v, i) => `${px(i)},${py(v)}`).join(' ');
  const baseY = py(start);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: '240px', display: 'block' }}>
      <line x1={P} x2={W - P} y1={baseY} y2={baseY} stroke="var(--border)" strokeDasharray="4 4" strokeWidth="1" />
      <polyline points={pts} fill="none" stroke="var(--accent)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// One bar per trading day (empty slot on no-pick days). Hover shows detail, click jumps to the day card.
function DayBars({ daily, onPick }) {
  const [hover, setHover] = useState(null);
  if (!daily.length) return null;
  const W = 1000, H = 160, P = 4;
  const vals = daily.map(d => d.picks ? d.pct : 0);
  const hi = Math.max(0, ...vals), lo = Math.min(0, ...vals);
  const span = hi - lo || 1;
  const py = v => P + ((hi - v) / span) * (H - 2 * P);
  const slot = (W - 2 * P) / daily.length;
  const zeroY = py(0);
  const h = hover != null ? daily[hover] : null;
  return (
    <>
      <div style={{ ...s.muted, ...s.mono, height: '18px', marginBottom: '6px' }}>
        {h ? <>{h.date} · {h.picks} pick{h.picks === 1 ? '' : 's'}
          {h.picks ? <> · <span style={{ color: col(h.pct) }}>{pct(h.pct)} ({money(h.end - h.start)})</span></> : ' · no trades'}</>
          : 'Hover a bar for detail, click to jump to that day'}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: `${H}px`, display: 'block' }}
           onMouseLeave={() => setHover(null)}>
        <line x1={P} x2={W - P} y1={zeroY} y2={zeroY} stroke="var(--border)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {daily.map((d, i) => {
          const v = vals[i], y = Math.min(py(v), zeroY);
          return (
            <g key={d.date} onMouseEnter={() => setHover(i)} onClick={() => d.picks && onPick(d.date)}
               style={{ cursor: d.picks ? 'pointer' : 'default' }}>
              <rect x={P + i * slot} y={0} width={slot} height={H} fill="transparent" />
              {d.picks > 0 && (
                <rect x={P + i * slot + slot * 0.15} width={slot * 0.7} y={y}
                      height={Math.max(Math.abs(py(v) - zeroY), 1)} fill={col(v)}
                      opacity={hover == null || hover === i ? 1 : 0.45} />
              )}
            </g>
          );
        })}
      </svg>
    </>
  );
}

function TradeHead({ first = 'Stock' }) {
  return (
    <div style={{ ...s.tradeGrid, ...s.colHead, padding: '0 12px 8px' }}>
      <div>{first}</div><div>Announcement</div><div>Sig</div><div>Open → Close</div>
      <div /><div style={s.right}>Return</div><div style={s.right}>P&amp;L</div>
    </div>
  );
}

function TradeRow({ t, scale, date }) {
  return (
    <div style={s.tradeGrid}>
      <div style={{ ...s.mono, fontWeight: 600 }}>{t.symbol}</div>
      <div style={s.headline} title={`${t.name} - ${t.headline}`}>
        {date && <span style={{ ...s.muted, ...s.mono, marginRight: '8px' }}>{date}</span>}
        {t.url
          ? <a href={t.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{t.headline}</a>
          : t.headline}
        <span style={{ ...s.muted, marginLeft: '8px', fontSize: '12px' }}>{fmtT(t.announced)}</span>
      </div>
      <div style={s.mono}>{t.sig ?? '—'}</div>
      <div style={{ ...s.mono, color: 'var(--text-muted)' }}>${t.open.toFixed(3)} → ${t.close.toFixed(3)}</div>
      <PctBar v={t.pct} scale={scale} />
      <div style={{ ...s.mono, ...s.right, color: col(t.pct), fontWeight: 600 }}>{pct(t.pct)}</div>
      <div style={{ ...s.mono, ...s.right, color: col(t.pnl) }}>{money(t.pnl)}</div>
    </div>
  );
}

function StockList({ title, rows, scale }) {
  return (
    <div>
      <div style={{ ...s.sectionTitle, fontSize: '13px', marginBottom: '8px' }}>{title} ({rows.length})</div>
      <div style={{ ...s.stockGrid, ...s.colHead, padding: '0 12px 8px' }}>
        <div>Stock</div><div>Trades</div><div>Win</div><div>Avg return</div><div /><div style={s.right}>Total P&amp;L</div>
      </div>
      {rows.map(t => (
        <div key={t.symbol} style={s.stockGrid} title={t.name}>
          <div style={{ ...s.mono, fontWeight: 600 }}>{t.symbol}</div>
          <div style={s.mono}>{t.trades}</div>
          <div style={s.mono}>{Math.round(t.wins / t.trades * 100)}%</div>
          <PctBar v={t.avg_pct} scale={scale} />
          <div style={{ ...s.mono, color: col(t.avg_pct) }}>{pct(t.avg_pct)}</div>
          <div style={{ ...s.mono, ...s.right, color: col(t.pnl) }}>{money(t.pnl)}</div>
        </div>
      ))}
      {!rows.length && <div style={{ ...s.muted, padding: '8px 12px' }}>None</div>}
    </div>
  );
}

export default function AdminBacktest() {
  const [days, setDays]     = useState('100');
  const [start, setStart]   = useState('100000');
  const [minSig, setMinSig] = useState('3');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [tradeView, setTradeView] = useState('all');   // all | win | lose
  const [dayView, setDayView]     = useState('all');   // all | win | lose

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const q = new URLSearchParams({ days, start, min_sig: minSig });
      const r = await api.get(`/admin/backtest/preopen-asx?${q}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Backtest failed');
    } finally {
      setLoading(false);
    }
  }, [days, start, minSig]);

  const sum = data?.summary;

  // Every trade with its date, best first; stats and the shared bar scale.
  const stats = useMemo(() => {
    if (!data) return null;
    const all = data.daily.flatMap(d => d.trades.map(t => ({ ...t, date: d.date })))
      .sort((a, b) => b.pct - a.pct);
    const abs = all.map(t => Math.abs(t.pct)).sort((a, b) => a - b);
    const scale = Math.max(abs[Math.floor(abs.length * 0.95)] || 0, 1);
    const wins = all.filter(t => t.pct > 0), losses = all.filter(t => t.pct < 0);
    const bySig = {};
    all.forEach(t => {
      const r = bySig[t.sig] || (bySig[t.sig] = { sig: t.sig, trades: 0, wins: 0, pcts: [], pnl: 0 });
      r.trades += 1; r.wins += t.pct > 0; r.pcts.push(t.pct); r.pnl += t.pnl;
    });
    return {
      all, scale,
      avg: mean(all.map(t => t.pct)),
      avgWin: mean(wins.map(t => t.pct)),
      avgLoss: mean(losses.map(t => t.pct)),
      bySig: Object.values(bySig).sort((a, b) => b.sig - a.sig).map(r => ({ ...r, avg: mean(r.pcts) })),
    };
  }, [data]);

  const jump = d => document.getElementById(`day-${d}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const keep = (view, v) => view === 'all' || (view === 'win' ? v > 0 : v < 0);
  const views = [['all', 'All'], ['win', 'Winners'], ['lose', 'Losers']];
  const dayViews = [['all', 'All'], ['win', 'Winning days'], ['lose', 'Losing days']];
  const tradeRows = stats ? stats.all.filter(t => keep(tradeView, t.pct)) : [];
  const dayCards = data ? [...data.daily].reverse().filter(d => d.picks && keep(dayView, d.pct)) : [];

  const tile = (label, value, color) => (
    <div style={s.tile}><div style={s.tileLabel}>{label}</div><div style={{ ...s.tileValue, color }}>{value}</div></div>
  );

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <div style={s.heading}>ASX Pre-Open Backtest</div>
        <div style={{ ...s.muted, marginBottom: '24px' }}>
          Buys ASX announcements rated positive (significance ≥ min) lodged since the previous close, at the open, equal split, sells at the close. Compounds daily.
        </div>

        {error && <div style={s.err}>{error}</div>}

        <div style={s.section}>
          <div style={s.row}>
            <label style={s.muted}>Days <input style={s.input} type="number" min="1" max="500" value={days} onChange={e => setDays(e.target.value)} /></label>
            <label style={s.muted}>Start $ <input style={s.input} type="number" min="1" value={start} onChange={e => setStart(e.target.value)} /></label>
            <label style={s.muted}>Min sig <input style={s.input} type="number" min="1" max="5" value={minSig} onChange={e => setMinSig(e.target.value)} /></label>
            <button style={{ ...s.btn, opacity: loading ? 0.5 : 1 }} disabled={loading} onClick={run}>
              {loading ? 'Running…' : 'Run'}
            </button>
          </div>
        </div>

        {sum && stats && (
          <>
            <div style={s.section}>
              <div style={s.tiles}>
                {tile('End balance', money(sum.end), col(sum.total_pct))}
                {tile('Total return', pct(sum.total_pct), col(sum.total_pct))}
                {tile('Win rate', sum.win_rate != null ? sum.win_rate + '%' : '—')}
                {tile('Avg per trade', stats.avg != null ? pct(stats.avg) : '—', col(stats.avg || 0))}
                {tile('Avg winner', stats.avgWin != null ? pct(stats.avgWin) : '—', 'var(--success)')}
                {tile('Avg loser', stats.avgLoss != null ? pct(stats.avgLoss) : '—', 'var(--error)')}
                <div style={s.tile}><div style={s.tileLabel}>Trades</div><div style={s.tileValue}>{sum.trades}<span style={{ ...s.muted, fontSize: '13px' }}> +{sum.skipped} skipped</span></div></div>
                <div style={s.tile}><div style={s.tileLabel}>Days traded</div><div style={s.tileValue}>{sum.days_with_picks}<span style={{ ...s.muted, fontSize: '13px' }}> / {sum.trading_days}</span></div></div>
                {tile('Best day', sum.best_day ? pct(sum.best_day.pct) : '—', col(sum.best_day?.pct || 0))}
                {tile('Worst day', sum.worst_day ? pct(sum.worst_day.pct) : '—', col(sum.worst_day?.pct || 0))}
              </div>
            </div>

            <div style={s.section}>
              <div style={{ ...s.sectionTitle, marginBottom: '16px' }}>Equity curve</div>
              <Curve days={data.daily.map(d => ({ date: d.date, balance: d.end }))} start={sum.start} />
              <div style={{ ...s.sectionTitle, margin: '24px 0 8px' }}>Daily results</div>
              <DayBars daily={data.daily} onPick={d => { setDayView('all'); setTimeout(() => jump(d), 0); }} />
            </div>

            <div style={s.section}>
              <div style={{ ...s.sectionTitle, marginBottom: '16px' }}>By rating</div>
              <div style={{ ...s.rateGrid, ...s.colHead, padding: '0 12px 8px' }}>
                <div>Sig</div><div>Trades</div><div>Win rate</div><div>Avg return</div><div /><div style={s.right}>Total P&amp;L</div>
              </div>
              {stats.bySig.map(r => (
                <div key={r.sig} style={s.rateGrid}>
                  <div style={{ ...s.mono, fontWeight: 600 }}>{r.sig}</div>
                  <div style={s.mono}>{r.trades}</div>
                  <div style={s.mono}>{Math.round(r.wins / r.trades * 100)}%</div>
                  <PctBar v={r.avg} scale={stats.scale} />
                  <div style={{ ...s.mono, color: col(r.avg) }}>{pct(r.avg)}</div>
                  <div style={{ ...s.mono, ...s.right, color: col(r.pnl) }}>{money(r.pnl)}</div>
                </div>
              ))}
            </div>

            <div style={s.section}>
              <div style={s.sectionHead}>
                <div style={s.sectionTitle}>All trades, best to worst ({tradeRows.length})</div>
                <Seg value={tradeView} onChange={setTradeView} options={views} />
              </div>
              <TradeHead />
              {tradeRows.map(t => <TradeRow key={`${t.date}-${t.symbol}`} t={t} scale={stats.scale} date={t.date} />)}
            </div>

            <div style={s.section}>
              <div style={{ ...s.sectionTitle, marginBottom: '16px' }}>By stock ({data.tickers.length})</div>
              <div style={s.twoCol}>
                <StockList title="Net winners" rows={data.tickers.filter(t => t.pnl > 0)} scale={stats.scale} />
                <StockList title="Net losers" rows={data.tickers.filter(t => t.pnl <= 0).reverse()} scale={stats.scale} />
              </div>
            </div>

            <div style={s.section}>
              <div style={s.sectionHead}>
                <div style={s.sectionTitle}>Days ({dayCards.length})</div>
                <Seg value={dayView} onChange={setDayView} options={dayViews} />
              </div>
              {dayCards.map(d => (
                <div key={d.date} id={`day-${d.date}`} style={{ ...s.dayCard, borderLeft: `3px solid ${col(d.pct)}` }}>
                  <div style={s.dayHead}>
                    <span style={{ fontWeight: 600 }}>{fmtD(d.date)}</span>
                    <span style={s.muted}>{d.picks} pick{d.picks === 1 ? '' : 's'}</span>
                    <span style={{ ...s.mono, color: col(d.pct), fontWeight: 600 }}>{pct(d.pct)}</span>
                    <span style={{ ...s.mono, color: col(d.pct) }}>{money(d.end - d.start)}</span>
                    <span style={{ ...s.muted, ...s.mono, marginLeft: 'auto' }}>Balance {money(d.end)}</span>
                  </div>
                  {[...d.trades].sort((a, b) => b.pct - a.pct).map(t => <TradeRow key={t.symbol} t={t} scale={stats.scale} />)}
                </div>
              ))}
              {!dayCards.length && <div style={s.muted}>No days match.</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
